/**
 * ストリーミングダウンロードユーティリティ
 *
 * fetch + ReadableStream を使用した進捗付きダウンロードの共通処理
 */

import type { EpisodeList } from '@/api/type'
import {
  clearDownloadChunks,
  discardAllPendingDownloads,
  getDownloadChunks,
  getDownloadResumeInfo,
  putDownloadChunk,
  upsertPendingDownload,
} from '@/lib/db'
import { formatBytes } from '@/lib/format-utils'

export type DownloadProgress = {
  loaded: number
  total: number
  percentage: number
}

/** IndexedDB へ書き出す 1 チャンクのサイズ（これ以上はメモリに載せない） */
const CHUNK_FLUSH_BYTES = 8 * 1024 * 1024

/** 進捗コールバックの最小間隔（ms） */
const PROGRESS_THROTTLE_MS = 250

/** サーバから応答があったうえで失敗したことを表すエラー */
export class DownloadHttpError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`HTTP error! status: ${status}`)
    this.name = 'DownloadHttpError'
    this.status = status
  }

  /** リトライで回復し得るか（一時的なサーバ側の問題か） */
  get isRetryable(): boolean {
    return this.status === 408 || this.status === 429 || this.status >= 500
  }
}

/** HTML が返ってきていないか検証する（SPA fallback / キャッシュされたエラーページの検出） */
const assertNotHtmlResponse = (response: Response) => {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('text/html')) return

  throw new Error(
    `Expected binary content but received HTML (content-type: ${contentType}). ` +
      'Service Workerのキャッシュをクリアしてください。',
  )
}

/**
 * URLからデータをストリーミングダウンロードし、進捗をコールバックで通知
 *
 * @returns ダウンロードしたデータのArrayBuffer
 */
export const fetchWithProgress = async (
  url: string,
  onProgress?: (progress: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<ArrayBuffer> => {
  const response = await fetch(url, { signal })

  if (!response.ok) {
    throw new DownloadHttpError(response.status)
  }

  assertNotHtmlResponse(response)

  const contentLength = response.headers.get('content-length') ?? response.headers.get('Content-Length')
  const total = contentLength ? parseInt(contentLength, 10) : 0

  if (!response.body || total === 0) {
    // Content-LengthがないかReadableStream非対応の場合は通常のfetch
    const buffer = await response.arrayBuffer()
    onProgress?.({ loaded: buffer.byteLength, total: buffer.byteLength, percentage: 100 })
    return buffer
  }

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let loaded = 0

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    chunks.push(value)
    loaded += value.length
    const percentage = total > 0 ? Math.round((loaded / total) * 1000) / 10 : 0
    onProgress?.({ loaded, total, percentage })
  }

  // chunksを結合してArrayBufferに変換
  const allChunks = new Uint8Array(loaded)
  let position = 0
  for (const chunk of chunks) {
    allChunks.set(chunk, position)
    position += chunk.length
  }

  return allChunks.buffer
}

// ---------------------------------------------------------------------------
// レジューム対応ダウンロード（大容量ファイル向け）
// ---------------------------------------------------------------------------

export type ResumableDownloadOptions = {
  /** 中間チャンクの保存キー */
  episodeId: number
  productId: number
  domain: string
  title: string
  /** 生成する Blob の MIME タイプ */
  mimeType: string
  /** ダウンロードする画質（中断再開時に同じ画質を使うために記録する） */
  quality?: string
  /** 署名付きURLを解決する関数（レジューム時に期限切れを避けるため都度呼ぶ） */
  resolveUrl: () => Promise<string>
  onProgress?: (progress: DownloadProgress) => void
  signal?: AbortSignal
}

/** Content-Range: bytes 100-199/1234 から全体サイズを取り出す */
const parseContentRangeTotal = (header: string | null): number => {
  if (!header) return 0
  const total = header.split('/').at(1)
  if (!total || total === '*') return 0
  const parsed = parseInt(total, 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

/** 現在の空き容量を取得する（推定できない場合は null） */
const getAvailableStorage = async (): Promise<number | null> => {
  if (!navigator.storage?.estimate) return null

  try {
    const { quota = 0, usage = 0 } = await navigator.storage.estimate()
    if (quota === 0) return null

    return quota - usage
  } catch (error) {
    // 権限やコンテキストによっては reject される。推定不能として扱いダウンロードは継続する
    console.warn('[Download] storage.estimate() に失敗しました:', error)
    return null
  }
}

/**
 * 保存に必要な空き容量があるか検証する
 *
 * 不足している場合は、他エピソードの中断中ダウンロード（再開されないまま残ったゴミ）を
 * 破棄してから再判定する。
 */
const assertStorageCapacity = async (requiredBytes: number, currentEpisodeId: number) => {
  if (requiredBytes <= 0) return

  const available = await getAvailableStorage()
  if (available === null || available >= requiredBytes) return

  const freed = await discardAllPendingDownloads(currentEpisodeId)
  if (freed > 0) {
    console.log(`[Download] 中断中ダウンロードを破棄して ${formatBytes(freed)} を解放しました`)
  }

  const retried = await getAvailableStorage()
  if (retried === null || retried >= requiredBytes) return

  throw new Error(`保存容量が不足しています（必要: ${formatBytes(requiredBytes)} / 空き: ${formatBytes(retried)}）`)
}

/** 実行中のダウンロード（同一エピソードの並行実行によるチャンク破損を防ぐ） */
const activeDownloadIds = new Set<number>()

/**
 * 大容量ファイルをレジューム対応でダウンロードする
 *
 * - 受信データは CHUNK_FLUSH_BYTES ごとに IndexedDB へ書き出すため、
 *   ヒープ使用量はファイルサイズによらず一定に保たれる
 * - 中断した場合は保存済みチャンクを残し、次回 Range リクエストで続きから再開する
 * - 返した Blob は中間チャンクを参照しているため、保存が完了するまで呼び出し側で
 *   discardPendingDownload() を実行しないこと
 */
export const downloadWithResume = async (options: ResumableDownloadOptions): Promise<Blob> => {
  if (activeDownloadIds.has(options.episodeId)) {
    throw new Error('このエピソードは既にダウンロード中です')
  }

  activeDownloadIds.add(options.episodeId)
  try {
    return await runDownload(options, false)
  } finally {
    activeDownloadIds.delete(options.episodeId)
  }
}

/**
 * @param isRetry 416（Range 範囲外）を受けてやり直している最中か。無限再帰の防止に使う
 */
const runDownload = async (options: ResumableDownloadOptions, isRetry: boolean): Promise<Blob> => {
  const { episodeId, productId, domain, title, mimeType, quality, resolveUrl, onProgress, signal } = options

  const resume = await getDownloadResumeInfo(episodeId)
  const url = await resolveUrl()

  const response = await fetch(url, {
    headers: resume.bytes > 0 ? { Range: `bytes=${resume.bytes}-` } : undefined,
    signal,
  })

  // 保存済み範囲がサーバ側のファイルサイズを超えている（ファイル差し替え等）→ 最初からやり直す
  if (response.status === 416) {
    await response.body?.cancel()
    if (isRetry) {
      throw new DownloadHttpError(416)
    }
    await clearDownloadChunks(episodeId)
    return runDownload(options, true)
  }

  if (!response.ok) {
    throw new DownloadHttpError(response.status)
  }

  assertNotHtmlResponse(response)

  // 206 が返らない場合はサーバが Range 非対応 → 既存チャンクを破棄して先頭から取得する
  const isPartial = response.status === 206
  if (resume.bytes > 0 && !isPartial) {
    await clearDownloadChunks(episodeId)
  }

  const offset = isPartial ? resume.bytes : 0
  let nextIndex = isPartial ? resume.nextIndex : 0

  const contentLength = parseInt(response.headers.get('content-length') ?? '0', 10)
  const total = isPartial
    ? parseContentRangeTotal(response.headers.get('content-range')) || offset + contentLength
    : contentLength

  // 完了時に中間チャンクと結合後 Blob が一時的に同居するため、残り + 全体サイズ分を要求する
  await assertStorageCapacity(total > 0 ? total - offset + total : 0, episodeId)

  const writePendingMeta = (receivedBytes: number) =>
    upsertPendingDownload({ domain, episodeId, mimeType, productId, quality, receivedBytes, title, totalBytes: total })

  if (!response.body) {
    // ReadableStream 非対応環境のフォールバック（レジューム不可なので全体を1度に取得する）
    await clearDownloadChunks(episodeId)
    const fullResponse = isPartial ? await fetch(url, { signal }) : response
    const blob = await fullResponse.blob()
    onProgress?.({ loaded: blob.size, percentage: 100, total: blob.size })
    return new Blob([blob], { type: mimeType })
  }

  const reader = response.body.getReader()
  let pending: BlobPart[] = []
  let pendingSize = 0
  let loaded = offset
  let lastNotifiedAt = 0

  const flush = async () => {
    if (pendingSize === 0) return

    await putDownloadChunk({
      blob: new Blob(pending),
      episodeId,
      index: nextIndex,
      size: pendingSize,
    })
    nextIndex += 1
    pending = []
    pendingSize = 0
    // 中断時に UI から再開／削除できるようメタ情報を更新する
    await writePendingMeta(loaded)
  }

  const notifyProgress = (force: boolean) => {
    const now = Date.now()
    if (!force && now - lastNotifiedAt < PROGRESS_THROTTLE_MS) return

    lastNotifiedAt = now
    const percentage = total > 0 ? Math.round((loaded / total) * 1000) / 10 : 0
    onProgress?.({ loaded, percentage, total })
  }

  await writePendingMeta(offset)
  notifyProgress(true)

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      // ArrayBufferLike を ArrayBuffer へ揃えるためのコピー（1チャンク分のみ）
      pending.push(new Uint8Array(value))
      pendingSize += value.length
      loaded += value.length

      if (pendingSize >= CHUNK_FLUSH_BYTES) {
        await flush()
      }
      notifyProgress(false)
    }
  } catch (error) {
    // 中断・通信断でループを抜けた場合はストリームを解放する（done 後の cancel は no-op）
    await reader.cancel().catch(() => undefined)
    throw error
  }

  await flush()
  notifyProgress(true)

  // 保存済みチャンクを結合（Blob の結合は参照ベースなのでメモリコピーは発生しない）
  // チャンクの削除は、返した Blob の保存が終わってから呼び出し側が行う
  const chunks = await getDownloadChunks(episodeId)
  return new Blob(
    chunks.map((chunk) => chunk.blob),
    { type: mimeType },
  )
}

// ---------------------------------------------------------------------------
// ダウンロードアイテムマッピング
// ---------------------------------------------------------------------------

/** EpisodeList配列からダウンロード用パラメータ配列を生成する */
export const buildDownloadItems = (episodes: EpisodeList[], productId: number, domain: string) =>
  episodes.map((ep) => ({
    episodeId: ep.episode_id,
    productId,
    domain,
    title: ep.custom_name ?? `Episode ${ep.index}`,
    mimeType: ep.mime_type ?? 'application/octet-stream',
  }))
